import type { JobLevel, JobType } from '@lib/enums/job-type.enum'
import { JOB_TYPE_ABBREVIATIONS } from '@/field-map/markerStyles'
import { cn } from '@/lib/utils'

/**
 * The job-type shield from the printed site maps, as vector art.
 *
 * These paths are not a redrawing — they were extracted from the "Job details
 * legend" layer of `tmp/The Trails at Regal Park Map layers final.pdf`, which
 * turned out to be a layered vector PDF with the badge artwork drawn once per
 * type. So a pin on a phone and a pin on the PDF a client already has in a
 * drawer are the same shape, to four decimal places, rather than two people's
 * best efforts at the same idea.
 *
 * All four badges shared one geometry in the source — the four placements
 * differed by at most 0.0012pt across a 14.78pt badge, which is float noise —
 * so the shape lives here once and only the shield's fill varies.
 *
 * The word and the level are live text rather than outlines, exactly as in the
 * original. That is the better half of the bargain: the text stays crisp at any
 * size, and the level is a real value from the record instead of the hardcoded
 * "1" every badge on the printed map carries.
 */

/** The artwork's own units. Everything else is expressed as a multiple. */
const VIEW_W = 14.78
const VIEW_H = 16.95

/** The two small tabs the band's ends fold into, then the band itself. */
const TAB_LEFT = 'M -0 5.69L 1.58 7.15L 1.58 11.79L -0 10.32Z'
const TAB_RIGHT = 'M 13.29 2.26L 14.78 3.27L 14.78 7.91L 13.29 6.89Z'
const BAND = 'M -0 5.69L 14.78 3.27L 14.78 7.91L -0 10.32Z'
/**
 * The shield's outer silhouette, filled white behind the ring.
 *
 * The colored shield in the source is a RING — outer contour and inner hole —
 * because print puts white paper behind it. Over aerial photography that hole
 * shows grass, and the badge turns into a faint outline round a picture of the
 * ground. Painting the silhouette first restores the page the artwork assumes.
 */
const SHIELD_SOLID = 'M 1.74 1.11C 2.86 1.05 3.82 0.93 4.7 0.76C 5.56 0.59 6.33 0.37 7.07 0.11L 7.39 0L 7.71 0.11C 8.45 0.37 9.22 0.59 10.08 0.76C 10.95 0.93 11.92 1.05 13.03 1.11L 13.78 1.14L 13.78 11.62L 13.62 11.83C 12.86 12.85 11.97 13.75 10.99 14.55C 10.01 15.35 8.95 16.05 7.84 16.69L 7.39 16.95L 6.94 16.69C 5.82 16.05 4.77 15.35 3.79 14.55C 2.81 13.75 1.92 12.85 1.16 11.83L 1 11.62L 1 1.14Z'

const SHIELD = 'M 1.74 1.11C 2.86 1.05 3.82 0.93 4.7 0.76C 5.56 0.59 6.33 0.37 7.07 0.11L 7.39 0L 7.71 0.11C 8.45 0.37 9.22 0.59 10.08 0.76C 10.95 0.93 11.92 1.05 13.03 1.11L 13.78 1.14L 13.78 11.62L 13.62 11.83C 12.86 12.85 11.97 13.75 10.99 14.55C 10.01 15.35 8.95 16.05 7.84 16.69L 7.39 16.95L 6.94 16.69C 5.82 16.05 4.77 15.35 3.79 14.55C 2.81 13.75 1.92 12.85 1.16 11.83L 1 11.62L 1 1.14ZM 4.99 2.3C 4.26 2.44 3.46 2.55 2.57 2.62L 2.57 11.1C 3.22 11.93 3.97 12.67 4.79 13.34C 5.58 13.99 6.46 14.58 7.39 15.13C 8.32 14.58 9.2 13.99 9.99 13.34C 10.81 12.67 11.56 11.93 12.21 11.1L 12.21 2.62C 11.32 2.55 10.52 2.44 9.79 2.3C 8.92 2.13 8.14 1.92 7.39 1.66C 6.64 1.92 5.85 2.13 4.99 2.3'

/**
 * Baselines lifted from the PDF's own text matrices, so the word and the level
 * sit exactly where the designer put them.
 *
 * The word carries a shear, straight from the source text matrix
 * `4 0.6335 0 4` — a ratio of 0.6335/4, or about 9°. That lean is what makes
 * the word sit parallel to the band, which is itself tilted; set the word
 * square and the badge looks subtly broken without it being obvious why.
 *
 * The sign is negated against the PDF because SVG's y axis points down where
 * the PDF's points up.
 *
 * `textLength` with `lengthAdjust="spacingAndGlyphs"` squeezes each word into
 * the same span of band. The original was set in a condensed face, which this
 * app does not ship, so at the app's own font "RESTORE" ran off the end of the
 * shield and rendered as "RESTO". Fitting to a fixed width reproduces what the
 * source does anyway — its longer words are visibly narrower than its short
 * ones.
 */
const WORD = { x: 2.431, y: 9.026, size: 4, shear: 0.1584, width: 9.9 }
const LEVEL = { x: 6.534, y: 13.538, size: 5 }

/** Tailwind fill classes, written out in full for the class scanner. */
const SHIELD_FILL: Record<JobType, string> = {
  CROWN_CLEANING: 'fill-map-clean',
  CROWN_RAISE: 'fill-map-raise',
  CROWN_RESTORATION: 'fill-map-restore',
  CROWN_THIN: 'fill-map-thin',
  STRUCTURAL_CLEARANCE: 'fill-map-clearance',
  OTHER: 'fill-map-other',
}

export interface JobBadgeProps {
  type: JobType
  level: JobLevel
  /** Rendered height in px. The width follows the artwork's aspect ratio. */
  height: number
  /**
   * Draw the job word on the band.
   *
   * Off on a map pin, where the band is about three pixels tall and the word
   * would be a smudge rather than a word — the shape and hue already say which
   * job it is, and the legend decodes them. On in the legend and the tree
   * editor, where there is room to read it.
   */
  showWord?: boolean
  className?: string
}

/**
 * One job badge: a colored shield with a black band across it.
 *
 * Far narrower than the text chip it replaces — about 14px against 40 — which
 * is what lets four of them sit under a pin without the strip setting the
 * width of the whole marker.
 */
export function JobBadge({
  type,
  level,
  height,
  showWord = false,
  className,
}: JobBadgeProps) {
  const width = (height * VIEW_W) / VIEW_H
  const fill = SHIELD_FILL[type]

  return (
    <svg
      viewBox={`0 0 ${String(VIEW_W)} ${String(VIEW_H)}`}
      width={width}
      height={height}
      className={cn('shrink-0', className)}
      role="img"
      aria-label={`${JOB_TYPE_ABBREVIATIONS[type]} level ${String(level)}`}
    >
      <path d={SHIELD_SOLID} className="fill-map-halo" />
      <path d={SHIELD} className={fill} />
      <path d={TAB_LEFT} className="fill-map-ink" fillRule="evenodd" />
      <path d={TAB_RIGHT} className="fill-map-ink" fillRule="evenodd" />
      <path d={BAND} className="fill-map-ink" fillRule="evenodd" />
      {showWord ? (
        <text
          fontSize={WORD.size}
          fontWeight={700}
          textLength={WORD.width}
          lengthAdjust="spacingAndGlyphs"
          className="fill-map-halo"
          transform={`matrix(1 ${String(-WORD.shear)} 0 1 ${String(WORD.x)} ${String(WORD.y)})`}
        >
          {JOB_TYPE_ABBREVIATIONS[type]}
        </text>
      ) : null}
      <text
        x={LEVEL.x}
        y={LEVEL.y}
        fontSize={LEVEL.size}
        fontWeight={700}
        textAnchor="middle"
        className={fill}
      >
        {level}
      </text>
    </svg>
  )
}
